<script setup lang="ts">
/**
 * First step of the guided flow: what is happening, why, and what to have
 * ready — worded for the customer's situation (view.journey).
 */
import { computed } from "vue";
import type { KycDocumentType, KycView } from "../../composables/useCompanyKyc";
import { inSentence } from "./docGuide";

const props = defineProps<{ view: KycView; plan: KycDocumentType[] }>();
const emit = defineEmits<{ (e: "start"): void; (e: "finish"): void; (e: "later"): void }>();

const company = computed(() => props.view.company.company_name);
const journey = computed(() => props.view.journey);
const gateOn = computed(() => !!props.view.order_gate?.enabled);
const required = computed(() => props.plan.filter((t) => t.is_required));
const optional = computed(() => props.plan.filter((t) => !t.is_required));
const toReplace = computed(() => props.plan.filter((t) => t.document?.review_status === "rejected" || t.document?.expired));
const missing = computed(() => props.plan.filter((t) => t.is_required && !t.document));

const fmtDay = (ymd: string | null) =>
  ymd ? new Date(`${ymd}T00:00:00`).toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric" }) : "";

// Back on the welcome after everything went for review (the live journey is
// now in_review, with nothing left to send): say so rather than ask again.
// Under review only a missing required document can be uploaded, so that
// alone decides it there (an expired optional one doesn't hold up a review).
const sent = computed(() =>
  journey.value === "in_review"
    ? !missing.value.length
    : ["replace", "expired"].includes(journey.value) && !toReplace.value.length && !missing.value.length,
);

const copy = computed(() => {
  const n = toReplace.value.length;
  if (sent.value) {
    return {
      eyebrow: "Verification",
      title: "We have your documents",
      lede: "We're reviewing what you sent. You can look back over each document, or see what happens next.",
      cta: "See what happens next",
    };
  }
  switch (journey.value) {
    case "requested":
      return {
        eyebrow: "Verification request",
        title: `Please verify ${company.value}`,
        lede: "We're asking every business account to confirm its registration with a few documents. Your account, orders and statements stay exactly as they are.",
        cta: "Start",
      };
    case "replace":
      return {
        eyebrow: "Action needed",
        title: n === 1 ? "One document needs replacing" : `${n} documents need replacing`,
        lede:
          n === 1
            ? "We reviewed your documents and couldn't accept the one below. Everything else is accepted and stays as it is — you only need to send this one again."
            : "We reviewed your documents and couldn't accept the ones below. Everything else is accepted and stays as it is — you only need to send these again.",
        cta: n === 1 ? "Replace it" : "Replace them",
      };
    case "expired":
      return {
        eyebrow: "Keep your company verified",
        title: n === 1 ? `Your ${inSentence(toReplace.value[0]?.label || "document")} has expired` : `${n} of your documents have expired`,
        lede: `To keep ${company.value} verified, upload the current ${n === 1 ? "one" : "ones"}. It only takes a minute.`,
        cta: n === 1 ? "Upload the current one" : "Upload the current ones",
      };
    case "verified":
      return {
        eyebrow: "Verified",
        title: `Add a document for ${company.value}`,
        lede: "Your company is verified and stays verified. A document you add now waits for Xobo to check it.",
        cta: "Continue",
      };
    case "in_review": {
      // Under review, but a type was made required after they sent theirs.
      const m = missing.value.length;
      return {
        eyebrow: "One more document",
        title:
          m === 1
            ? `We also need your ${inSentence(missing.value[0]?.label || "document")}`
            : `We also need ${m} more documents`,
        lede: "We're reviewing what you sent. Since then we've started asking every business for this, and we can't finish the review without it.",
        cta: m === 1 ? "Upload it" : "Upload them",
      };
    }
    default:
      return {
        eyebrow: "Business verification",
        title: `Let's verify ${company.value}`,
        lede: "Business accounts are verified with a few registration documents. With them to hand, it takes about five minutes.",
        cta: "Start",
      };
  }
});

const orderLine = computed(() => {
  if (journey.value === "verified") return "You can keep placing orders.";
  if (!gateOn.value) return "You can keep placing orders while we review.";
  return journey.value === "first_time"
    ? "You can place orders as soon as we've approved them."
    : "Orders are paused until we've approved them.";
});
</script>

<template>
  <section class="vw vf-card">
    <div class="vw-hero" :class="`is-${journey}`">
      <span class="vw-hero__icon" aria-hidden="true">
        <svg v-if="journey === 'replace'" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7" /><polyline points="21 3 21 9 15 9" /></svg>
        <svg v-else-if="journey === 'expired'" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /><line x1="10" y1="14" x2="14" y2="18" /><line x1="14" y1="14" x2="10" y2="18" /></svg>
        <svg v-else width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></svg>
      </span>
      <p class="vf-eyebrow vw-hero__eyebrow">{{ copy.eyebrow }}</p>
      <h1 class="vw-hero__title" tabindex="-1">{{ copy.title }}</h1>
      <p class="vw-hero__lede">{{ copy.lede }}</p>
    </div>

    <div class="vw-body">
      <!-- Replace / expired / one more needed: exactly what to send, and why -->
      <template v-if="journey === 'replace' || journey === 'expired' || journey === 'in_review'">
        <p v-if="view.company.kyc_rejection_note && journey === 'replace'" class="vw-note">
          <span class="vw-note__label">Note from Xobo</span>
          {{ view.company.kyc_rejection_note }}
        </p>
        <ul v-if="!sent && (toReplace.length || missing.length)" class="vw-issues">
          <li v-for="t in toReplace" :key="t.doc_type" class="vw-issue">
            <span class="vw-issue__dot" aria-hidden="true">!</span>
            <div>
              <strong>{{ t.label }}</strong>
              <span v-if="t.document?.rejection_reason_code === 'doc_expired' || t.document?.expired">
                Expired{{ t.document?.expires_at ? ` on ${fmtDay(t.document.expires_at)}` : "" }}.
              </span>
              <span v-else>{{ t.document?.rejection_reason_label || "Not accepted" }}<template v-if="t.document?.rejection_note"> — {{ t.document.rejection_note }}</template></span>
            </div>
          </li>
          <li v-for="t in missing" :key="t.doc_type" class="vw-issue">
            <span class="vw-issue__dot" aria-hidden="true">+</span>
            <div><strong>{{ t.label }}</strong><span>Now required — not uploaded yet.</span></div>
          </li>
        </ul>
      </template>

      <!-- First time / requested: what to have ready (a verified company
           adding one document has nothing to prepare) -->
      <template v-else-if="journey !== 'verified'">
        <h2 class="vw-h2">What you'll need</h2>
        <ul class="vw-docs">
          <li v-for="t in required" :key="t.doc_type" class="vw-doc" :class="{ 'is-done': t.document && t.document.review_status !== 'rejected' }">
            <span class="vw-doc__icon" aria-hidden="true">
              <svg v-if="t.document && t.document.review_status !== 'rejected'" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
              <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
            </span>
            <span class="vw-doc__label">{{ t.label }}</span>
            <span v-if="t.document && t.document.review_status !== 'rejected'" class="vw-doc__state">Sent</span>
          </li>
        </ul>
        <p v-if="optional.length" class="vw-optional">
          <strong>Optional:</strong> {{ optional.map((t) => t.label).join(" · ") }}. Add them if you have them; you can skip each one.
        </p>
      </template>

      <ul class="vw-facts">
        <li>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>
          {{ orderLine }}
        </li>
        <li>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
          PDFs or clear photos — a phone camera is fine. Up to 10 MB each.
        </li>
        <li>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
          Stored privately: only your admins and our verification team can open them.
        </li>
      </ul>

      <div class="vw-actions">
        <button type="button" class="vf-btn" @click="sent ? emit('finish') : emit('start')">
          {{ copy.cta }}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
        </button>
        <button type="button" class="vf-btn vf-btn--link" @click="emit('later')">{{ sent ? "Back to Xobo" : "I'll do this later" }}</button>
      </div>
    </div>
  </section>
</template>

<style scoped>
.vw { overflow: hidden; }
.vw-hero {
  position: relative;
  padding: 36px 40px 30px;
  background: linear-gradient(135deg, #12284a 0%, #1e3f73 100%);
  color: #fff;
  overflow: hidden;
}
.vw-hero::after {
  content: "";
  position: absolute;
  right: -60px;
  top: -80px;
  width: 260px;
  height: 260px;
  border-radius: 50%;
  border: 36px solid rgba(255, 255, 255, 0.05);
  pointer-events: none;
}
.vw-hero.is-replace,
.vw-hero.is-expired { background: linear-gradient(135deg, #3b1d1d 0%, #12284a 85%); }
.vw-hero__icon {
  width: 54px;
  height: 54px;
  border-radius: 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.22);
  margin-bottom: 18px;
}
.vw-hero__eyebrow { color: rgba(255, 255, 255, 0.7); }
.vw-hero__title { margin: 0; font-size: 1.75rem; line-height: 1.2; font-weight: 700; letter-spacing: -0.01em; }
.vw-hero__lede { margin: 10px 0 0; max-width: 58ch; font-size: 0.98rem; line-height: 1.6; color: rgba(255, 255, 255, 0.82); }

.vw-body { padding: 28px 40px 32px; display: flex; flex-direction: column; gap: 22px; }
.vw-h2 { margin: 0 0 -8px; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--vf-text); }
.vw-docs { list-style: none; margin: 0; padding: 0; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.vw-doc {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid var(--vf-line);
  border-radius: 13px;
  background: #fbfcfe;
}
.vw-doc__icon {
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vf-info-bg);
  color: var(--vf-navy);
}
.vw-doc.is-done .vw-doc__icon { background: var(--vf-ok-bg); color: var(--vf-ok); }
.vw-doc__label { flex: 1; font-size: 0.9rem; font-weight: 600; line-height: 1.35; }
.vw-doc__state { font-size: 0.74rem; font-weight: 700; color: var(--vf-ok); }
.vw-optional { margin: -6px 0 0; font-size: 0.84rem; color: var(--vf-text); line-height: 1.55; }

.vw-note {
  margin: 0;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--vf-info-bg);
  font-size: 0.9rem;
  color: var(--vf-ink);
  line-height: 1.55;
}
.vw-note__label { display: block; margin-bottom: 2px; font-size: 0.7rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--vf-navy-2); }
.vw-issues { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.vw-issue {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  border: 1px solid #f3c9c9;
  border-left: 4px solid var(--vf-bad);
  border-radius: 12px;
  background: var(--vf-bad-bg);
}
.vw-issue__dot {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--vf-bad);
  color: #fff;
  font-size: 0.8rem;
  font-weight: 800;
}
.vw-issue strong { display: block; font-size: 0.92rem; color: var(--vf-ink); }
.vw-issue div span { font-size: 0.86rem; color: #7f1d1d; line-height: 1.5; }

.vw-facts { list-style: none; margin: 0; padding: 18px 0 0; border-top: 1px solid var(--vf-line); display: flex; flex-direction: column; gap: 10px; }
.vw-facts li { display: flex; align-items: flex-start; gap: 10px; font-size: 0.88rem; color: var(--vf-text); line-height: 1.5; }
.vw-facts svg { flex-shrink: 0; margin-top: 2px; color: var(--vf-navy-2); }

.vw-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding-top: 4px; }

@media (max-width: 640px) {
  .vw-hero { padding: 26px 20px 22px; }
  .vw-hero__title { font-size: 1.4rem; }
  .vw-body { padding: 22px 20px 24px; }
  .vw-docs { grid-template-columns: minmax(0, 1fr); }
  .vw-actions .vf-btn:not(.vf-btn--link) { width: 100%; }
}
</style>
